import {Dispatch, SetStateAction, useState} from 'react';
import {ApiError, useBackendClient} from '@legit.health/backend-client';
import {blobToBase64, generateRandomId, ImageWithCrop} from '@legit.health/ui';
import {TFunction} from 'i18next';
import type {BodyPart, BodyPartCode, FormValues} from '../interfaces';

interface ValidateImageResponse {
  isValid: boolean;
  qualityAcceptable: boolean;
  isDermatological: boolean;
  diqaScore: number;
}
interface ValidatePoseResponse {
  isValid: boolean;
}

export default function useImageValidation(t: TFunction) {
  const backendClient = useBackendClient();
  const [imageValidationError, setImageValidationError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  function resetImageValidationError() {
    setImageValidationError(null);
  }

  function handleImageChange(
    field: BodyPartCode,
    imageWithCrop: ImageWithCrop | null,
    setForm: Dispatch<SetStateAction<FormValues>>
  ) {
    setForm((prev) => {
      if (imageWithCrop && imageWithCrop.crop && imageWithCrop.file) {
        return {
          ...prev,
          [field]: {
            crop: imageWithCrop.crop,
            file: imageWithCrop.file,
            id: prev[field].id,
            base64: null
          }
        };
      }

      return {
        ...prev,
        [field]: {
          crop: null,
          file: null,
          id: generateRandomId(),
          base64: null
        }
      };
    });
    resetImageValidationError();
  }

  async function validateImage(
    bodyPart: BodyPart,
    form: FormValues,
    setForm: Dispatch<SetStateAction<FormValues>>
  ) {
    const currentImage = form[bodyPart.code];
    if (!currentImage.file || !currentImage.crop) {
      return false;
    }

    setIsValidating(true);
    resetImageValidationError();

    try {
      const base64Image = currentImage.base64 ?? (await blobToBase64(currentImage.file));

      if (!currentImage.base64) {
        setForm((prev) => ({
          ...prev,
          [bodyPart.code]: {...prev[bodyPart.code], base64: base64Image}
        }));
      }

      const imagePayload = {
        image: base64Image,
        x: currentImage.crop.x,
        y: currentImage.crop.y,
        width: currentImage.crop.width,
        height: currentImage.crop.height
      };

      const imageValidationResponse = await backendClient.sendRequest<ValidateImageResponse>({
        method: 'POST',
        path: '/validate-image',
        body: {image: imagePayload}
      });

      if (!imageValidationResponse.data.isValid) {
        const errorKey = !imageValidationResponse.data.isDermatological
          ? 'isDermatologyDomain'
          : 'qualityAcceptable';
        setImageValidationError(errorKey);
        setIsValidating(false);
        return false;
      }
      if (bodyPart.validationCode) {
        const poseValidationResponse = await backendClient.sendRequest<ValidatePoseResponse>({
          method: 'POST',
          path: '/validate-pose',
          body: {
            image: imagePayload,
            pose: bodyPart.validationCode
          }
        });
        if (!poseValidationResponse.data.isValid) {
          setImageValidationError('invalidPose');
          setIsValidating(false);
          return false;
        }
      }

      setIsValidating(false);
      return true;
    } catch (error) {
      if (error instanceof ApiError) {
        setImageValidationError(t('ui:diagnosticReport:serverError'));
      }
      setIsValidating(false);
      return false;
    }
  }

  return {
    imageValidationError,
    isValidating,
    handleImageChange,
    validateImage
  };
}
